import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { invitesApi } from '@/lib/invitesApi';
import { toast } from '@/components/ui/sonner';
import { Users, Eye, Check, EnvelopeSimple, DownloadSimple, BellRinging, ArrowLeft, Megaphone, Trash, ChatCircleText } from '@phosphor-icons/react';

const Stat = ({ label, value, tone }) => (
  <div className="bg-card rounded-xl border border-border/70 shadow-soft p-4 text-center">
    <div className={`text-2xl font-display font-semibold ${tone || 'text-foreground'}`}>{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-0.5">{label}</div>
  </div>
);

export default function RsvpDashboardPage() {
  const { eventId } = useParams();
  const { token } = useAuth();
  const [invId, setInvId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [publicId, setPublicId] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [wall, setWall] = useState([]);
  const [broadcast, setBroadcast] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const ev = await invitesApi.getEvent(eventId, token);
    const first = ev.invitations?.[0];
    if (!first) { setLoading(false); return; }
    setInvId(first.invitation_id);
    setPublicId(first.public_id || null);
    const d = await invitesApi.rsvpDashboard(first.invitation_id, token);
    setData(d);
    if (first.public_id) invitesApi.live(first.public_id).then((l) => { setAnnouncements(l.announcements || []); setWall(l.wall || []); }).catch(() => {});
    setLoading(false);
  }, [eventId, token]);

  useEffect(() => { load().catch(() => setLoading(false)); }, [load]);

  const sendBroadcast = async () => {
    const t = broadcast.trim();
    if (!t) return;
    setSending(true);
    try {
      const r = await invitesApi.announce(invId, { text: t }, token);
      setBroadcast('');
      if (r.announcement) setAnnouncements((a) => [r.announcement, ...a]);
      setNote('Update posted to your guests.');
      setTimeout(() => setNote(''), 3000);
    } catch { setNote('Could not post — try again.'); setTimeout(() => setNote(''), 3000); }
    finally { setSending(false); }
  };
  const deleteAnnouncement = async (id) => {
    setAnnouncements((a) => a.filter((x) => x.id !== id));
    invitesApi.deleteAnnounce(invId, id, token).catch(() => {});
  };

  const [reminding, setReminding] = useState(false);
  const remind = async () => {
    if (reminding) return;
    // Optional personal line the host can add to the nudge.
    const msg = window.prompt("Add a short personal note to the reminder? (optional — leave blank to skip)", "");
    if (msg === null) return;   // host cancelled
    setReminding(true);
    try {
      const r = await invitesApi.remind(invId, token, msg.trim() || undefined);
      if (r.reminded === 0 && r.skipped > 0) {
        setNote(`Everyone still pending was already reminded in the last day — no repeat emails sent.`);
      } else if (r.reminded === 0) {
        setNote(`No pending guests to remind — everyone's responded! 🎉`);
      } else {
        setNote(`Reminder sent to ${r.reminded} guest${r.reminded === 1 ? '' : 's'}${r.skipped ? ` (${r.skipped} skipped — reminded recently)` : ''}.`);
      }
    } catch {
      setNote('Could not send reminders — please try again.');
    } finally {
      setReminding(false);
      setTimeout(() => setNote(''), 5000);
    }
  };
  const checkin = async (gid) => {
    try { await invitesApi.checkin(invId, gid, token); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not check in this guest. Please try again.'); }
  };
  const exportCsv = async () => {
    try {
      const url = invitesApi.exportUrl(invId);
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      // Guard against saving an error page as "rsvps.csv" when the request fails.
      if (!r.ok) throw new Error(`export failed (${r.status})`);
      const b = await r.blob();
      const objUrl = URL.createObjectURL(b);
      const a = document.createElement('a'); a.href = objUrl; a.download = 'rsvps.csv'; a.click();
      URL.revokeObjectURL(objUrl);
    } catch (e) {
      toast.error('Could not export your guest list. Please try again.');
    }
  };

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="max-w-5xl mx-auto p-8"><div className="h-40 bg-secondary/50 rounded-2xl animate-pulse" /></div></div>;
  if (!data) return <div className="min-h-screen bg-background"><Navbar /><div className="max-w-5xl mx-auto p-8 text-center text-muted-foreground">No invitation to track yet. <Link to={`/events/${eventId}/invitations`} className="text-primary font-semibold">Create one →</Link></div></div>;

  const { counts, analytics, guests, questions } = data;
  // Guests who haven't responded and can receive an email reminder.
  const pendingWithEmail = (guests || []).filter(g => (g.rsvp_status || 'pending') === 'pending' && (g.email || '').trim()).length;

  return (
    <div className="min-h-screen bg-background" data-testid="rsvp-dashboard">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to={`/events/${eventId}/invitations`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft size={14} /> Back to builder</Link>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-foreground">RSVPs</h1>
          <div className="flex gap-2">
            <button onClick={remind} disabled={reminding || pendingWithEmail === 0}
              title={pendingWithEmail === 0 ? 'No guests are waiting to RSVP' : `Remind ${pendingWithEmail} guest${pendingWithEmail === 1 ? '' : 's'} who haven't responded`}
              className="inline-flex items-center gap-1.5 text-sm font-semibold border border-border bg-card rounded-lg px-3 h-9 hover:bg-secondary/60 disabled:opacity-50 disabled:cursor-not-allowed">
              <BellRinging size={15} /> {reminding ? 'Sending…' : `Remind${pendingWithEmail ? ` (${pendingWithEmail})` : ''}`}
            </button>
            <button onClick={exportCsv} className="inline-flex items-center gap-1.5 text-sm font-semibold border border-border bg-card rounded-lg px-3 h-9 hover:bg-secondary/60"><DownloadSimple size={15} /> Export CSV</button>
          </div>
        </div>
        {note && <div className="mb-4 text-sm bg-primary/10 text-primary rounded-lg px-3 py-2">{note}</div>}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Stat label="Attending" value={counts.yes} tone="text-emerald-600" />
          <Stat label="Maybe" value={counts.maybe} tone="text-amber-600" />
          <Stat label="Can't make it" value={counts.no} tone="text-muted-foreground" />
          <Stat label="Head count" value={analytics.head_count} tone="text-primary" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <Stat label="Invited" value={analytics.invited} />
          <Stat label="Opened" value={analytics.opened} />
          <Stat label="Page views" value={analytics.views} />
          <Stat label="Responded" value={analytics.responded} />
        </div>

        {/* Day-of broadcast — post updates guests see live on the invite page */}
        <div className="bg-card rounded-2xl border border-border/70 shadow-soft overflow-hidden mb-6" data-testid="broadcast">
          <div className="px-4 py-3 border-b border-border/60 flex items-center gap-2"><Megaphone size={15} weight="fill" className="text-primary" /><span className="text-sm font-semibold">Live updates</span></div>
          <div className="p-4">
            <div className="flex gap-2">
              <input value={broadcast} onChange={(e) => setBroadcast(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !sending) sendBroadcast(); }}
                placeholder="Post an update — “Running 15 min late”, “Parking around back”…" className="flex-1 rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" maxLength={280} data-testid="broadcast-input" />
              <button onClick={sendBroadcast} disabled={sending || !broadcast.trim()} className="shrink-0 rounded-lg bg-primary text-primary-foreground px-4 text-sm font-semibold disabled:opacity-60" data-testid="broadcast-send">{sending ? '…' : 'Post'}</button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">Shows instantly on your guests’ invitation page on the day.</p>
            {announcements.length > 0 && (
              <div className="mt-3 space-y-2">
                {announcements.map((a) => (
                  <div key={a.id} className="flex items-start gap-2 rounded-lg bg-secondary/50 px-3 py-2">
                    <p className="flex-1 text-sm text-foreground leading-snug">{a.text}</p>
                    <button onClick={() => deleteAnnouncement(a.id)} className="text-muted-foreground hover:text-accent-coral shrink-0" aria-label="Delete update"><Trash size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border/70 shadow-soft overflow-hidden">
          <div className="px-4 py-3 border-b border-border/60 flex items-center gap-2"><Users size={15} className="text-primary" /><span className="text-sm font-semibold">Guests · {guests.length}</span></div>
          <div className="divide-y divide-border/50 max-h-[520px] overflow-y-auto">
            {guests.map((g) => {
              // Custom-question answers the guest gave (skip the built-in 'meal',
              // already shown, and any blank answers).
              const qa = (questions || [])
                .filter((q) => q.id !== 'meal' && (g.answers || {})[q.id])
                .map((q) => ({ label: q.label, value: (g.answers || {})[q.id] }));
              return (
                <div key={g.guest_id} className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${g.rsvp_status === 'yes' ? 'bg-emerald-500' : g.rsvp_status === 'maybe' ? 'bg-amber-500' : g.rsvp_status === 'no' ? 'bg-muted-foreground' : 'bg-border'}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{g.name || g.email || g.phone || 'Guest'}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{g.email}{g.plus_ones ? ` · +${g.plus_ones}` : ''}{g.meal ? ` · ${g.meal}` : ''}</p>
                    </div>
                    <span className="text-[11px] font-semibold capitalize text-muted-foreground w-14 text-right">{g.rsvp_status === 'pending' ? '—' : g.rsvp_status}</span>
                    <button onClick={() => checkin(g.guest_id)} title="Check in" className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${g.checked_in ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}><Check size={13} weight="bold" /></button>
                  </div>
                  {/* Custom-question answers — previously only in the CSV export */}
                  {qa.length > 0 && (
                    <div className="mt-1.5 ml-5 flex flex-wrap gap-1.5" data-testid={`answers-${g.guest_id}`}>
                      {qa.map((a, i) => (
                        <span key={i} className="text-[11px] bg-secondary/60 rounded-md px-2 py-0.5 text-foreground/80">
                          <span className="text-muted-foreground">{a.label}:</span> {a.value}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {!guests.length && <p className="text-sm text-muted-foreground text-center py-10">No guests yet.</p>}
          </div>
        </div>

        {/* Guest wall — messages guests posted on the invite page (host-visible) */}
        {wall.length > 0 && (
          <div className="bg-card rounded-2xl border border-border/70 shadow-soft overflow-hidden mt-6" data-testid="guest-wall">
            <div className="px-4 py-3 border-b border-border/60 flex items-center gap-2">
              <ChatCircleText size={15} weight="fill" className="text-primary" />
              <span className="text-sm font-semibold">Guest wall · {wall.length}</span>
            </div>
            <div className="divide-y divide-border/50 max-h-[360px] overflow-y-auto">
              {wall.map((w) => (
                <div key={w.id} className="px-4 py-3">
                  <p className="text-sm text-foreground leading-snug">{w.text}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">— {w.name || 'A guest'}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
